import store from '@/store';
import api, { localizedUrl } from '@/api';
import log from '@/configured/logger';
import {
  isBootstrapRequest,
  normalizeBootstrapOptions,
  runSharedLoad,
} from '@/base-data/shared-load';

const exports = {
  namespace: 'training',
  module: {
    namespaced: true,
    state: {
      loadedAll: false,
      trainingLanding: [],
      webinars: [],
      videos: [],
      courseCatalog: [],
      usersCourses: [],
      availableCourses: [],
      inProgressCourses: [],
      fullLineCertifications: [],
      completedCoursesCount: 0,
      certificatesCount: 0,
    },
    mutations: {
      SET_ALL,
      SET_AVAILABLE_COURSES: (state, courses) =>
        (state.availableCourses = courses),
      SET_FULL_LINE_CERTS: (state, courses) =>
        (state.fullLineCertifications = courses),
      SET_IN_PROGRESS_COURSES: (state, courses) =>
        (state.inProgressCourses = courses),
      SET_COMPLETED_COURSES_COUNT: (state, count) =>
        (state.completedCoursesCount = count),
      SET_CERTIFICATES_COUNT: (state, count) =>
        (state.certificatesCount = count),
      CLEAR_CACHED_TRAINING: (state) => {
        state.loadedAll = false;
        state.trainingLanding = [];
        state.webinars = [];
        state.videos = [];
        state.courseCatalog = [];
        state.usersCourses = [];
        state.availableCourses = [];
        state.inProgressCourses = [];
        state.fullLineCertifications = [];
        state.completedCoursesCount = 0;
        state.certificatesCount = 0;
      },
    },
    actions: {
      loadTrainingDataIfNeeded,
      loadAll,
      reloadCachedTraining,
    },
  },
};

store.registerModule(exports.namespace.split('/'), exports.module);

export default exports;

//////////

async function loadTrainingDataIfNeeded(
  { state, commit },
  payload = false
) {
  const { forceInline, mode, bootstrapContext } =
    normalizeBootstrapOptions(payload);

  if (state.loadedAll && !forceInline) {
    if (isBootstrapRequest(bootstrapContext)) {
      return Promise.resolve();
    }

    log.info('loadTrainingDataIfNeeded: optimistic reload');
    store.dispatch(`${exports.namespace}/loadAll`);
    return Promise.resolve();
  }
  log.info('loadTrainingDataIfNeeded: needed data');
  const language = store.state.i18n.language || 'en';

  return runSharedLoad(
    `${exports.namespace}:${language}`,
    () => store.dispatch(`${exports.namespace}/loadAll`),
    {
      bootstrapContext,
      metricKey: exports.namespace,
    }
  );
}

async function loadAll({ state, commit }) {
  const language = store.state.i18n.language || 'en';

  return runSharedLoad(`${exports.namespace}:all:${language}`, () => {
    const loader = api
      .all([
        api.get(localizedUrl('training')),
        api.get(localizedUrl('training/webinars')),
        api.get(localizedUrl('training/videos')),
        api.get('/actions/mercury/talent-lms/courses'),
      ])
      .then(
        api.spread((landing, webinars, videos, courses) => {
          let courseCatalog = courses.data.allCourses;
          if (!courseCatalog) {
            courseCatalog = [];
          }

          let usersCourses = courses.data.usersCourses;
          if (!usersCourses) {
            usersCourses = [];
          }

          commit('SET_ALL', {
            trainingLanding: landing.data,
            webinars: webinars.data,
            videos: videos.data,
            courseCatalog: courseCatalog,
            usersCourses: usersCourses,
          });

          let allInProgress = state.usersCourses.filter(
            (course) => course.status != 'completed'
          );
          let inProgress = [];

          // Remove the courses from in progress that are not also in the catalog.
          // This can happen when people play around with course status in Talent's Dashboard.
          allInProgress.forEach((userCourse) => {
            let catalogCourse = state.courseCatalog.find(
              (catalogCourse) => userCourse.id === catalogCourse.id
            );
            if (catalogCourse) {
              // It's in the catalog so add it to the list.
              inProgress.push(userCourse);
            }
          });

          // Sort the in progress courses -- newest first
          function timestampSort(a, b) {
            if (a.startedTimestamp == b.startedTimestamp) {
              return 0;
            }

            if (a.startedTimestamp < b.startedTimestamp) {
              return 1;
            }

            return -1;
          }

          inProgress.sort(timestampSort);
          commit('SET_IN_PROGRESS_COURSES', inProgress);

          let completedCoursesCount = state.usersCourses.filter(
            (course) => course.status == 'completed'
          ).length;
          commit('SET_COMPLETED_COURSES_COUNT', completedCoursesCount);

          // Determine the available courses.
          // Copy the array
          let available = courseCatalog.slice(0);

          // Sort the available courses -- First by category, than subcategory, then name.
          function coursesSort(a, b) {
            let collator = new Intl.Collator(undefined, {
              numeric: true,
              sensitivity: 'base',
            });

            if (a.mainCategory == b.mainCategory) {
              if (a.subcategory == b.subcategory) {
                return collator.compare(a.title, b.title);
              }

              return collator.compare(a.subcategory, b.subcategory);
            }

            return collator.compare(a.mainCategory, b.mainCategory);
          }

          available.sort(coursesSort);

          commit('SET_AVAILABLE_COURSES', available);

          // Determine the users completed full line certifications.
          let fullLineCertifications = [];

          courseCatalog.forEach((course) => {
            let userCourse = state.usersCourses.find(
              (userCourse) => userCourse.id === course.id
            );
            if (userCourse) {
              if (course.isFullLineSummary && userCourse.completed) {
                fullLineCertifications.push(userCourse);
              }
            }
          });

          commit('SET_FULL_LINE_CERTS', fullLineCertifications);

          let certCount = state.usersCourses.filter(
            (course) => course.certificateUrl != null
          ).length;
          certCount += fullLineCertifications.length;
          commit('SET_CERTIFICATES_COUNT', certCount);
        })
      );

    return state.loadedAll ? Promise.resolve() : loader;
  });
}

function SET_ALL(state, data) {
  if (
    !state.trainingLanding ||
    JSON.stringify(data.trainingLanding) !==
      JSON.stringify(state.trainingLanding)
  ) {
    state.trainingLanding = data.trainingLanding;
  }
  if (
    !state.webinars ||
    JSON.stringify(data.webinars) !== JSON.stringify(state.webinars)
  ) {
    state.webinars = data.webinars;
  }
  if (
    !state.videos ||
    JSON.stringify(data.videos) !== JSON.stringify(state.videos)
  ) {
    state.videos = data.videos;
  }
  if (
    !state.courseCatalog ||
    JSON.stringify(data.courseCatalog) !== JSON.stringify(state.courseCatalog)
  ) {
    state.courseCatalog = data.courseCatalog;
  }
  if (
    !state.usersCourses ||
    JSON.stringify(data.usersCourses) !== JSON.stringify(state.usersCourses)
  ) {
    state.usersCourses = data.usersCourses;
  }
  state.loadedAll = true;
}

function reloadCachedTraining({ state, commit }) {
  log.info('Clearing cached training');
  commit('CLEAR_CACHED_TRAINING');
  loadAll({ state, commit });
}
